import React from 'react';
import PropTypes from 'prop-types';
import Chip from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Chip';
import { noop } from 'Helpers/PropTypes';
import 'emoji-mart/css/emoji-mart.css';
import { Picker } from 'emoji-mart';
import Popover from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Popover';
import usePopoverState from 'Hooks/usePopoverState';
import { Add, InsertEmoticon } from '@material-ui/icons';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { FDStack, FDBox, FDTooltip } from '@facet-wealth/design';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { DataValueMap } from 'Helpers/Testing';

const excludedEmojiCategories = [
  'flags',
];

// Unicode values for excluded emojis
const excludedEmojis = [
  '1F595', // :middle_finger:
  '1F346', // :eggplant:
  '1F351', // :peach:
  '1F4A9', // :poop:
];

const useStyles = buildUseStyles('AddReactionButton', (theme) => ({
  addChip: {
    display: 'flex',
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  addIcon: {
    color: theme.palette.facet.actionActive,
    fontSize: theme.typography.h4.fontSize,
  },
}));

const AddReactionButton = ({
  handleAdd,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);

  const {
    logEvent,
  } = useEventTrackingLogEvent({
    nodeName: 'Add Reaction Button',
  });

  const {
    open,
    anchorEl,
    openPopover,
    handleClose,
    handleEnter,
  } = usePopoverState({
    onClose: () => {
      logEvent('Messages: Closed the emoji picker');
    },
    onEnter: () => {
      logEvent('Messages: Opened the emoji picker');
    },
  });

  const filterExcludedEmojis = (emoji) => {
    return !excludedEmojis.includes(emoji?.unified);
  };

  return (
    <FDBox>
      <FDTooltip
        title={'Add reaction'}
        placement="top"
      >
        <Chip
          size="small"
          variant="outlined"
          className={classes.addChip}
          label={
            <FDStack>
              <InsertEmoticon className={classes.addIcon} fontSize="small" />
              <Add className={classes.addIcon} fontSize={'small'} />
            </FDStack>}
          onClick={openPopover}
          {...DataValueMap.init('add-reaction-button').build()}
        />
      </FDTooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        onEnter={handleEnter}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Picker
          autoFocus
          emoji=":smile:"
          title="Choose an emoji"
          color={theme.palette.primary.main}
          exclude={excludedEmojiCategories}
          emojisToShowFilter={filterExcludedEmojis}
          onSelect={async (emoji) => {
            await handleAdd(emoji?.colons);
            handleClose();
          }}
        />
      </Popover>
    </FDBox>
  );
};

AddReactionButton.defaultProps = {
  handleAdd: noop,
};

AddReactionButton.propTypes = {
  handleAdd: PropTypes.func,
};

export { AddReactionButton };
